import { IMedia } from "@/interfaces/media.interface";
import { MessageType } from "@/types/message.types";

const AUDIO_EXTENSIONS = ["mp3", "ogg", "oga", "opus", "wav", "webm", "m4a", "mp4a", "aac"];

const resolveMediaType = (ext: string | undefined): MessageType => {
  if (!ext) return "text";
  if (ext === "jpg" || ext === "png" || ext === "jpeg") return "image";
  if (ext === "pdf" || ext === "docx") return "document";
  if (ext === "mp4" || ext === "avi") return "video";
  if (AUDIO_EXTENSIONS.includes(ext)) return "audio";
  return "text";
};

export const getMediaType = (file: File): MessageType => {
  const ext = file.name.split(".").pop()?.toLowerCase();
  return resolveMediaType(ext);
};

export const getMediaTypeByString = (fileName: string): MessageType => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  return resolveMediaType(ext);
};

export const getExtensionFromName = (name: string) => {
  const parts = name.split(".");
  return parts[parts.length - 1];
};

export const getMimeType = (media: IMedia): string => {
  switch (media.type) {
    case "image":
      return `image/${media.extension}`;
    case "video":
      return `video/${media.extension}`;
    case "audio": {
      const ext = (media.extension || "").toLowerCase();
      if (ext === "mp3") return "audio/mpeg";
      if (ext === "ogg" || ext === "oga" || ext === "opus") return "audio/ogg";
      if (ext === "wav") return "audio/wav";
      if (ext === "webm") return "audio/webm";
      if (ext === "m4a" || ext === "mp4a" || ext === "aac") return "audio/mp4";
      return `audio/${ext || "mpeg"}`;
    }
    case "document":
      return `application/${media.extension}`;
    case "sticker":
      return "...";
    default:
      return "application/octet-stream"; // fallback para tipo binário
  }
};

export const base64ToBlob = (base64String: string, mimeType: string) => {
  const base64Data = base64String.split(",")[1];
  const byteCharacters = atob(base64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
};

export const base64ToBlobV2 = (base64: string, mimeType: string): Blob => {
  const byteCharacters = atob(base64);
  const byteArray = new Uint8Array([...byteCharacters].map((char) => char.charCodeAt(0)));
  return new Blob([byteArray], { type: mimeType });
};

export const isValidFileType = (filename: string): boolean => {
  const validExtensions = ["jpg", "jpeg", "png"];
  const extension = filename.split(".").pop()?.toLowerCase() || "";
  return validExtensions.includes(extension);
};

export const isFileSizeValid = (fileSize: number, maxSize: number = 2 * 1024 * 1024): boolean => {
  return fileSize <= maxSize;
};

export const isValidFileName = (filename: string): boolean => {
  const fileNameRegex = /^[^\s*]+$/;
  return fileNameRegex.test(filename.replace(/\.[^/.]+$/, ""));
};

export const validateImageDimensions = (
  img: HTMLImageElement,
  maxDimensions: number = 100000
): boolean => {
  return img.width <= maxDimensions && img.height <= maxDimensions;
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};
